import { getAccount, getDefaultAccountLanguage } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';

/** Bonus id fields accepted by UsersAPI `RegisterRequest`. */
export type RegisterBonusField =
  | 'FreeBetId'
  | 'BackupBetId'
  | 'CasinoBonusId'
  | 'SportsbookBonusId'
  | 'ExternalSportsbookBonusId';

export interface RegisterBonusIds {
  FreeBetId?: string;
  BackupBetId?: string;
  CasinoBonusId?: string;
  SportsbookBonusId?: string;
  ExternalSportsbookBonusId?: string;
}

export interface RegisterBonusItem {
  Id: string;
  Type: string;
  Name: string;
  PageUrl?: string | null;
  PageTitle?: string | null;
}

export interface RegisterBonusesResponse {
  Success?: boolean;
  Message?: string;
  Bonuses?: RegisterBonusItem[] | null;
}

export interface RegisterRequestPayload extends RegisterBonusIds {
  AccountGuid: string;
  Username: string;
  Password: string;
  Country: number;
  CurrencyId: number;
  Language: string;
  /** Optional on fast register (username-only mode). */
  Email?: string;
  /** Optional on fast register (username-only mode). */
  Telephone?: string;
  FirstName?: string;
  LastName?: string;
  /** ISO date `YYYY-MM-DD`. Omitted on fast register. */
  BirthDate?: string;
  Address?: string;
  ZipCode?: string;
  City?: string;
  CitizenId?: string;
  PromoCode?: string;
  AffiliateLinkId?: string;
}

export interface RegisterResponse {
  Success?: boolean;
  Guid?: string;
  Verify?: boolean;
  Error?: string;
}

export interface UniqueCheckRequest {
  AccountGuid: string;
  Username?: string;
  Email?: string;
  Telephone?: string;
}

export interface VerifySmsRequest {
  Guid: string;
  Phone: string;
  Code: string;
}

export interface ResendSmsRequest {
  MachineGuid: string;
  Phone: string;
}

export interface CommonSuccessResponse {
  Success?: boolean;
  Error?: string;
}

/**
 * Maps register-bonus `Type` strings from UsersAPI to the request field name.
 * FreeBet=`6`, BackupBet=`7`; wager register bonuses are prefixed `s`/`c`/`e`.
 */
export function mapBonusTypeToField(type: string | null | undefined): RegisterBonusField | null {
  if (typeof type !== 'string' || !type) return null;
  if (type === '6') return 'FreeBetId';
  if (type === '7') return 'BackupBetId';
  if (type.includes('c')) return 'CasinoBonusId';
  if (type.includes('s')) return 'SportsbookBonusId';
  if (type.includes('e')) return 'ExternalSportsbookBonusId';
  return null;
}

/** Builds bonus id payload for the selected encrypted bonus id + type. */
export function bonusIdsForSelection(
  bonus: Pick<RegisterBonusItem, 'Id' | 'Type'> | null | undefined,
): RegisterBonusIds {
  if (!bonus?.Id) return {};
  const field = mapBonusTypeToField(bonus.Type);
  if (!field) return {};
  return { [field]: bonus.Id };
}

function affiliateLinkIdFromStorage(): string | undefined {
  try {
    const value = sessionStorage.getItem('affiliateCookie');
    return value && value.trim() ? value.trim() : undefined;
  } catch {
    return undefined;
  }
}

/** True when the value has no spaces (legacy uniqueness pre-check). */
export function isUniqueCandidate(value: string): boolean {
  return value.length > 0 && !value.includes(' ');
}

export async function checkUnique(
  fields: Omit<UniqueCheckRequest, 'AccountGuid'> & { AccountGuid?: string },
  signal?: AbortSignal,
): Promise<boolean> {
  const account = getAccount();
  const accountGuid = fields.AccountGuid ?? account?.Guid;
  if (!accountGuid) return false;

  const payload: UniqueCheckRequest = { AccountGuid: accountGuid };
  if (fields.Username !== undefined) payload.Username = fields.Username;
  if (fields.Email !== undefined) payload.Email = fields.Email;
  if (fields.Telephone !== undefined) payload.Telephone = fields.Telephone;

  return api.post<boolean>(
    endpoints.player.unique.host,
    endpoints.player.unique.path,
    payload as unknown as Record<string, unknown>,
    signal,
  );
}

export async function fetchRegisterBonuses(
  countryId: number | string,
  signal?: AbortSignal,
): Promise<RegisterBonusItem[]> {
  const account = getAccount();
  if (!account?.Guid) return [];

  const response = await api.post<RegisterBonusesResponse>(
    endpoints.player.registerBonuses.host,
    endpoints.player.registerBonuses.path,
    {
      AccountGuid: account.Guid,
      CountryId: Number(countryId),
      Language: getDefaultAccountLanguage(account),
    },
    signal,
  );

  return Array.isArray(response.Bonuses) ? response.Bonuses : [];
}

export async function register(
  payload: Omit<RegisterRequestPayload, 'AccountGuid' | 'Language'> &
    Partial<Pick<RegisterRequestPayload, 'AccountGuid' | 'Language' | 'AffiliateLinkId'>>,
  signal?: AbortSignal,
): Promise<RegisterResponse> {
  const account = getAccount();
  const accountGuid = payload.AccountGuid ?? account?.Guid;
  if (!accountGuid) {
    return { Success: false, Error: 'Error Registering User' };
  }

  const body: RegisterRequestPayload = {
    ...payload,
    AccountGuid: accountGuid,
    Language: payload.Language ?? getDefaultAccountLanguage(account),
  };

  const affiliate = payload.AffiliateLinkId ?? affiliateLinkIdFromStorage();
  if (affiliate) body.AffiliateLinkId = affiliate;
  if (!body.PromoCode) delete body.PromoCode;
  if (!body.Email) delete body.Email;
  if (!body.Telephone) delete body.Telephone;
  if (!body.FirstName) delete body.FirstName;
  if (!body.LastName) delete body.LastName;
  if (!body.BirthDate) delete body.BirthDate;
  if (!body.Address) delete body.Address;
  if (!body.ZipCode) delete body.ZipCode;
  if (!body.City) delete body.City;
  if (!body.CitizenId) delete body.CitizenId;

  return api.post<RegisterResponse>(
    endpoints.player.register.host,
    endpoints.player.register.path,
    body as unknown as Record<string, unknown>,
    signal,
  );
}

export async function verifySms(
  request: VerifySmsRequest,
  signal?: AbortSignal,
): Promise<CommonSuccessResponse> {
  return api.post<CommonSuccessResponse>(
    endpoints.player.verifySms.host,
    endpoints.player.verifySms.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

export async function resendSms(request: ResendSmsRequest, signal?: AbortSignal): Promise<boolean> {
  return api.post<boolean>(
    endpoints.player.resendSms.host,
    endpoints.player.resendSms.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

export interface VerifyEmailRequest {
  Value: string;
}

/** Confirms an email verification token from a deep link. */
export async function verifyEmail(
  request: VerifyEmailRequest,
  signal?: AbortSignal,
): Promise<CommonSuccessResponse> {
  return api.post<CommonSuccessResponse>(
    endpoints.player.verifyEmail.host,
    endpoints.player.verifyEmail.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

/** Complex password: 8+, lower, upper, digit, special (legacy register pattern). */
export const COMPLEX_PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$^+=!*()@%&]).{8,}$/;

export function isAtLeast18(birthDate: Date, now = new Date()): boolean {
  if (Number.isNaN(birthDate.getTime())) return false;
  const eighteenth = new Date(
    birthDate.getFullYear() + 18,
    birthDate.getMonth(),
    birthDate.getDate(),
  );
  return eighteenth <= now;
}

/** Builds `YYYY-MM-DD` from padded day/month/year selects. */
export function composeBirthDate(day: string, month: string, year: string): string | null {
  if (!day || !month || !year) return null;
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
